import { createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"

import config from "@/config"

import { CollectPayment } from "../components/collect-payment/collect-payment"
import { PageError, PageLoading } from "../components/page-state/page-state"
import {
  collectPaymentAction,
  createCustomerAction,
  generateInvoicePdfAction,
  loadCustomers,
  loadInvoiceDocument,
  loadInvoicePdfUrl,
  loadInvoiceUsage,
  loadSellerProfile,
  updateSellerProfileAction,
} from "../rpc"
import { sydneyToday } from "../status"

export const Route = createFileRoute("/_app-shell/invoices/collect")({
  loader: async () => {
    const [seller, customers, usage] = await Promise.all([
      loadSellerProfile(),
      loadCustomers(),
      loadInvoiceUsage(),
    ])
    return { seller, customers, usage, today: sydneyToday(new Date()) }
  },
  head: () => ({
    meta: [
      { title: `Collect payment - ${config.core.projectName}` },
      { name: "robots", content: "noindex" },
    ],
  }),
  pendingComponent: () => <PageLoading title="Collect payment" label="Loading the form" />,
  errorComponent: ({ error }) => {
    const router = useRouter()
    return (
      <PageError
        title="Collect payment"
        message={error.message || "The form could not be loaded."}
        onRetry={() => router.invalidate()}
      />
    )
  },
  component: InvoiceCollectRoute,
})

function InvoiceCollectRoute() {
  const { seller, customers, usage, today } = Route.useLoaderData()
  const router = useRouter()
  const collect = useServerFn(collectPaymentAction)
  const createCustomer = useServerFn(createCustomerAction)
  const saveSeller = useServerFn(updateSellerProfileAction)
  const generatePdf = useServerFn(generateInvoicePdfAction)
  const pdfUrl = useServerFn(loadInvoicePdfUrl)
  const loadDocument = useServerFn(loadInvoiceDocument)

  return (
    <CollectPayment
      seller={seller}
      customers={customers}
      usage={usage}
      today={today}
      onCollect={async (request) => {
        const result = await collect({ data: request })
        // re-fetch (and wait) so the result page shows the quota after this invoice
        if (result.ok) await router.invalidate().catch(() => undefined)
        return result
      }}
      onCreateCustomer={(request) => createCustomer({ data: request })}
      onSaveSeller={async (request) => {
        const result = await saveSeller({ data: request })
        if (result.ok) void router.invalidate()
        return result
      }}
      onLoadDocument={(id) => loadDocument({ data: { id } })}
      onGeneratePdf={(id) => generatePdf({ data: { id } })}
      onPdfUrl={(id) => pdfUrl({ data: { id } })}
      onDone={() => router.navigate({ to: "/invoices" })}
    />
  )
}
